/**
 * @flow
 * @format
 */

import { pathJoin } from '../utils';

type ApiResponseErrorType = {
  message: string,
  name: string,
  response: {},
  stack: string,
};

export function APIResponseError(message: string = '') {
  this.name = 'APIResponseError';
  this.message = message;
  this.stack = new Error().stack;
  this.response = {};
}

APIResponseError.prototype = Object.create(Error.prototype);
APIResponseError.prototype.constructor = APIResponseError;

type ResponseType = {
  json(): Promise<any>,
  status: number,
  statusText: string,
};

export function checkStatus(response: ResponseType): ResponseType {
  console.log('checkStatus', response);
  if (response.status >= 200 && response.status <= 400) return response;

  // All errors >400 throw errors
  const error: ApiResponseErrorType = new APIResponseError(response.statusText);
  error.response = response;
  throw error;
}

const parseJSON = res => res.json();
const parseBlob = res => res.blob();
const parseText = res => res.text();

const endPoints = {
  currentUser: '/user/retrieveCurrentUser',
  newDesign: '/new',
  evaluate: '/evaluate',
  typelist: '/typeinfolist',
  records: '/records',
  singleRecord: '/retrieveRecordById',
  saveRecord: '/save',
  updateRecord: '/updateRecordById/',
  chartData: '/chartData',
  downloadPDF: '/design/boost-api/v1/report/downloadPDF',
  downloadSVG: '/design/boost-api/v1/schematic/svg',
};

const getFullPath = path =>
  pathJoin(
    [process.env.REACT_APP_API_BASE, process.env.REACT_APP_API_ROOT_PATH, path],
    '/',
  );

export function fetchGet(path: string): Promise<any> {
  const fullUrl = getFullPath(path);

  return fetch(fullUrl, {
    method: 'GET',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  })
    .then(checkStatus)
    .then(response => response.json())
    .then(response => ({ response }));
}

/**
 * Fetch helper function
 * fetch is a chain of promises
 * @param {string} path URL to fetch
 * @param {any} data payload
 * @param {promise} parseHandler promise to handle reponse
 */
export function fetchPost(
  path: string,
  data?: any = '',
  parseHandler?: any = parseJSON,
): Promise<any> {
  const fullUrl = path.includes('design/') ? path : getFullPath(path);

  return fetch(fullUrl, {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
    .then(checkStatus)
    .then(parseHandler)
    .then(response => ({ response }));
}

export async function fetchByMethod(
  path: string,
  method: 'POST' | 'COPY' | 'PUT' | 'HEAD' | 'DELETE' | 'PATCH',
  payload: string = '',
) {
  const fullUrl = path.includes('design/') ? path : getFullPath(path);

  // the fetchPost function does not work with this new post API... wtf
  const rawResponse = await fetch(fullUrl, {
    method,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: payload,
  });
  return rawResponse.json().then(response => ({ response }));
}

// export const fetchParts: Function = () => fetchGet(`${API_ROOT}/product/all`);
export const fetchCurrentUser = () => fetchGet(endPoints.currentUser);
export const fetchDefaultData = () => fetchGet(endPoints.newDesign);
export const fetchAllRecords = () => fetchGet(endPoints.records);

export const fetchRecord = (id: string) =>
  fetchGet(`${endPoints.singleRecord}/${id}`);

export const saveDesignRecord = (data: any) =>
  fetchPost(endPoints.saveRecord, data);

export const updateRecordName = async (payload: {
  id: number,
  name: string,
}) => {
  const path = pathJoin(
    [endPoints.updateRecord, payload.id, payload.name],
    '/',
  );
  return fetchByMethod(path, 'POST');
};

export const copyDesign = async (payload: { id: number, name: string }) => {
  const path = pathJoin([endPoints.records, payload.id, 'copy'], '/');
  return fetchByMethod(path, 'POST', JSON.stringify({ name: payload.name }));
};

export const updateDesign = async (payload: { id: number, data: any }) => {
  const path = pathJoin([endPoints.records, payload.id], '/');

  console.log('calling PUT', path, 'with', payload.data);
  return fetchByMethod(path, 'PUT', JSON.stringify(payload.data));
};

export const deleteDesign = async (id: number) => {
  const path = pathJoin([endPoints.records, id], '/');
  return fetchByMethod(path, 'DELETE');
};

export const fetchChartData = (data: any) =>
  fetchPost(endPoints.chartData, data);

export const evaluateData = (data: any) => fetchPost(endPoints.evaluate, data);

export const fetchSchematic = (data: any) =>
  fetchPost(endPoints.downloadSVG, data, parseText);

export const downloadReportPDF = (data: any) =>
  fetchPost(endPoints.downloadPDF, data, parseBlob);

const ref = `${document.location.origin}${process.env.REACT_APP_BASE_PATH}`;
const redirect = `${process.env.REACT_APP_API_LOGIN}?referer=${ref}`;
export const loginRedirect = () => {
  if (process.env.NODE_ENV === 'testing') return false;
  // Not sure if this is needed now that the delay is controled by the Saga
  return setTimeout(() => {
    window.location.href = redirect;
  }, 0);
};



// WEBPACK FOOTER //
// ./src/services/api.js